export interface ApiEnvelope<T> {
  ok: boolean
  data: T
}

export interface SessionState {
  authenticated: boolean
  user: {
    id: string
    email: string | null
    name: string | null
    avatarUrl: string | null
    isAdgiroEmployee: boolean
  } | null
  org: {
    id: string
    name: string
    role: string
  } | null
}

export interface DashboardOverview {
  siteCount: number
  connectedChannelCount: number
  activeCampaignCount: number
  latestSiteMetricDate: string | null
  latestPrimaryConversions: number
  latestSpendAmount: string | null
  latestRevenueAmount: string | null
  latestCurrency: string | null
  latestOptimizationSummary: string | null
}

export interface PublicLaunchPreviewAsset {
  id: string
  type: "headline" | "description" | "keyword_theme" | "negative_keyword" | "image"
  text?: string
  imageUrl?: string
  title?: string
}

export interface PublicLaunchPreview {
  previewId: string
  websiteUrl: string
  primaryDomain: string
  budgetPlan: string
  monthlyPriceCents: number
  includedAdSpendCents: number
  managementFeeCents: number
  autoOptimize: boolean
  goal: string
  locationText: string | null
  status: string
  summary: {
    businessName: string
    offer: string
    audience: string
    landingPage: string
    angle: string
  }
  assets: PublicLaunchPreviewAsset[]
  convertedSiteId: string | null
  expiresAt: string
  createdAt: string
}

export interface AdminUserSummary {
  userId: string
  email: string | null
  name: string | null
  avatarUrl: string | null
  isAdgiroEmployee: boolean
  createdAt: string
  updatedAt: string
  orgCount: number
  providerConnectionCount: number
}

export interface SiteRecord {
  siteId: string
  orgId: string
  name: string
  primaryDomain: string | null
  primaryGoal: SiteGoal
  automationMode: SiteAutomationMode
  growthSettings: SiteGrowthSettings | null
  defaultLocations: Record<string, unknown> | null
  defaultAudience: Record<string, unknown> | null
  landingPageCount: number
  summary?: SiteListSummary
  createdAt: string
  updatedAt: string
}

export type SiteGoal = "traffic" | "leads" | "purchases" | "not_sure"
export type LandingPageGoal = SiteGoal | "inherit"
export type SiteAutomationMode = "manual" | "assist" | "full_auto"
export type SiteChannelRollupState =
  | "missing"
  | "draft"
  | "live"
  | "paused"
  | "needs_sync"
  | "failed"

export interface SiteListSummary {
  assetCount: number
  segmentCount: number
  connectedAdsAccountCount: number
  activeCampaignCount: number
  liveCampaignCount: number
  pausedCampaignCount: number
  needsSyncCount: number
  failedSyncCount: number
  spendMicros30Days: number
  conversions30Days: number
  clicks30Days: number
  latestStatsAt: string | null
  channels: {
    googleSearch: SiteChannelRollupState
    googlePmax: SiteChannelRollupState
    metaAds: SiteChannelRollupState
  }
}

export interface SiteGrowthSettings {
  dailyBudgetCap?: string | null
  experimentBudgetCap?: string | null
  targetCostPerPrimaryConversion?: string | null
  currency?: string | null
}

export interface SiteLandingPageRecord {
  siteLandingPageId: string
  siteId: string
  url: string
  label: string
  goal: LandingPageGoal
  locations: Record<string, unknown> | null
  audience: Record<string, unknown> | null
  vertical: string | null
  status: string
  createdAt: string
  updatedAt: string
}

export interface ProviderConnectionSummary {
  providerConnectionId: string
  provider: string
  connectionType: string
  externalAccountId: string | null
  scope: string | null
  capabilities: ProviderConnectionCapabilities
  status: string
  lastVerifiedAt: string | null
  createdAt: string
  updatedAt: string
  providerAccountCount: number
  linkedSiteCount: number
}

export interface ProviderConnectionCapabilities {
  canReadAds: boolean
  canManageAnalytics: boolean
  canManageSearchConsole: boolean
}

export interface ProviderAccountRecord {
  providerAccountId: string
  providerConnectionId: string
  provider: string
  accountType: string
  externalAccountId: string
  label: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export type SiteProviderRole = "ads" | "analytics" | "search_console"

export interface SiteProviderLinkRecord {
  siteProviderLinkId: string
  siteId: string
  provider: string
  providerAccountId: string
  role: SiteProviderRole
  createdAt: string
  account: ProviderAccountRecord
}

export interface SiteProviderCandidateRecord extends ProviderAccountRecord {
  role: SiteProviderRole
  linked: boolean
  matchRank: number
  matchReasons: string[]
}

export interface SiteProviderLinksPayload {
  links: SiteProviderLinkRecord[]
  candidates: SiteProviderCandidateRecord[]
  measurementParentAccounts: ProviderAccountRecord[]
}

export interface SearchConsoleSetupPayload {
  status: "linked" | "reconnect_required" | "cloudflare_available" | "manual_required"
  domain: string
  searchConsoleSiteUrl: string
  authoritativeNameservers: string[]
  nameserversAreCloudflare: boolean
  dnsRecord: {
    type: "TXT"
    host: string
    value: string
  } | null
  reconnectUrl: string | null
  cloudflareInstall: {
    available: boolean
    url: string | null
    reason: string | null
  }
}

export interface SearchConsoleVerifyPayload {
  linked: boolean
  link: SiteProviderLinkRecord | null
  setup?: SearchConsoleSetupPayload
}

export type SiteAssetType =
  | "landing_page"
  | "location"
  | "audience"
  | "competitor"
  | "search_intent"
  | "keyword_theme"
  | "negative_keyword"
  | "headline"
  | "description"
  | "offer"
  | "proof_point"
  | "site_link"
  | "image"
  | "video"

export interface SiteSegmentRecord {
  segmentId: string
  siteId: string
  parentSegmentId: string | null
  name: string
  segmentType: "default" | "segment"
  status: string
  assetCount: number
  createdAt: string
  updatedAt: string
}

export interface SiteAssetRecord {
  assetId: string
  siteId: string
  assetType: SiteAssetType
  name: string
  status: string
  source: string
  valueText: string | null
  valueUrl: string | null
  valueJson: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
  segmentRole: string | null
  sortOrder: number
}

export type LocationResolutionStatus = "resolved" | "partial" | "ambiguous" | "failed"

export interface NormalizedLocationRecord {
  label: string
  countryCode: string
  regionCode: string | null
  regionName: string | null
  locality: string | null
  latitude: number | null
  longitude: number | null
  radiusMiles: number | null
}

export interface ProviderLocationResolutionRecord {
  status: "not_linked" | "resolved" | "failed"
  message: string
  providerAccountIds: string[]
  locations?: ProviderLocationMatchRecord[]
}

export interface ProviderLocationMatchRecord {
  provider?: string
  providerAccountId?: string
  id?: string
  key?: string
  resourceName?: string
  name?: string
  canonicalName?: string
  countryCode?: string | null
  targetType?: string
  type?: string
  region?: string | null
  regionId?: string | number | null
}

export interface LocationResolutionRecord {
  kind: "location"
  status: LocationResolutionStatus
  query: string
  normalized: NormalizedLocationRecord | null
  candidates: NormalizedLocationRecord[]
  providers: Record<string, ProviderLocationResolutionRecord>
  message: string | null
  resolvedAt: string
}

export interface ResolvedLocationAssetsResult {
  assets: SiteAssetRecord[]
  resolutions: LocationResolutionRecord[]
}

export interface GeneratedLandingPageChoice {
  url: string
  label: string
  confidence: number
  reason: string
}

export interface GeneratedLandingPagesResult {
  model: string
  choices: GeneratedLandingPageChoice[]
  assets: SiteAssetRecord[]
  skippedExistingUrls: string[]
}

export interface GeneratedCopyChoice {
  assetType: SiteAssetType
  text: string
  rationale: string
}

export interface GeneratedCopyAssetsResult {
  model: string
  choices: GeneratedCopyChoice[]
  assets: SiteAssetRecord[]
  skippedExistingCopy: string[]
}

export interface QueuedGenerationJobResult {
  job: JobRecord | null
  blockedByActiveSync?: ActiveCampaignSyncBlocker | null
}

export interface QueuedSegmentGenerationJobsResult {
  jobs: {
    copy: JobRecord
    targeting: JobRecord
    media: JobRecord
  }
  reusedMediaAssets: SiteAssetRecord[]
}

export type CampaignChannelKey = "google_search" | "google_pmax" | "meta_ads"

export interface CampaignReadinessAction {
  label: string
  scope: "landing" | "targeting" | "copy" | "media"
  assetTypes: SiteAssetType[]
}

export interface CampaignReadinessItem {
  key: "ads_accounts" | "meta_page" | "landing" | "targeting" | "copy" | "site_links" | "media"
  label: string
  ready: boolean
  points: number
  maxPoints: number
  message: string
  action: CampaignReadinessAction | null
}

export interface CampaignChannelReadiness {
  channel: CampaignChannelKey
  label: string
  score: number
  ready: boolean
  items: CampaignReadinessItem[]
  missing: string[]
}

export interface CampaignReadiness {
  score: number
  ready: boolean
  connectedAdsAccountCount: number
  eligibleChannels: CampaignChannelKey[]
  channelReadiness: CampaignChannelReadiness[]
  items: CampaignReadinessItem[]
  missing: string[]
}

export interface CampaignChannelRecord {
  campaignChannelId: string
  orgId: string
  siteId: string
  segmentId: string
  conversionDefinitionId: string | null
  provider: string
  channel: CampaignChannelKey
  providerAccountId: string | null
  providerSupportUrl: string | null
  externalCampaignId: string | null
  name: string | null
  status: string
  syncState: string
  syncJobId: string | null
  lastSyncError: string | null
  objective: string
  budgetAmount: string | null
  budgetCurrency: string | null
  config: Record<string, unknown> | null
  sourceAssetHash: string | null
  sourceAssetSnapshot: Record<string, unknown> | null
  lastSyncedAt: string | null
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  latestSnapshot?: CampaignChannelSnapshotRecord | null
}

export interface CampaignEntityRecord {
  campaignEntityId: string
  parentCampaignEntityId: string | null
  campaignChannelId: string
  provider: string
  providerAccountId: string | null
  entityType: string
  externalEntityId: string
  externalParentEntityId: string | null
  name: string | null
  status: string
  metadata: Record<string, unknown> | null
  lastSyncedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface CampaignChannelSnapshotRecord {
  snapshotId: string
  campaignChannelId: string
  siteId: string
  capturedAt: string
  status: string
  dateRange: string
  summary: Record<string, unknown> | null
  r2Key: string
  createdAt: string
}

export interface OptimizationRunRecord {
  optimizationRunId: string
  orgId: string
  siteId: string
  campaignChannelId: string | null
  mode: string
  status: string
  summary: string | null
  inputs: Record<string, unknown> | null
  output: Record<string, unknown> | null
  startedAt: string | null
  completedAt: string | null
  createdAt: string
  updatedAt: string
}

export interface AgentActionRecord {
  agentActionId: string
  orgId: string
  siteId: string
  campaignChannelId: string | null
  optimizationRunId: string | null
  provider: string | null
  actionType: string
  targetType: string | null
  targetId: string | null
  status: string
  title: string
  rationale: string | null
  request: Record<string, unknown> | null
  response: Record<string, unknown> | null
  error: Record<string, unknown> | null
  createdAt: string
  executedAt: string | null
}

export interface GoogleSearchTermDiagnostic {
  searchTerm: string
  matchedKeyword: string | null
  adGroupResourceName: string | null
  matchType: string | null
  impressions: number
  clicks: number
  costMicros: number
  conversions: number
  conversionValue: number
}

export interface GoogleSearchNegativeDiagnostic {
  text: string
  matchType: "BROAD" | "EXACT" | "PHRASE" | null
  resourceName: string | null
}

export interface GoogleSearchSpendSummary {
  campaignCostMicros30Days: number
  returnedSearchTermCostMicros30Days: number
  visibleSearchTermCostMicros30Days: number
  hiddenReturnedSearchTermCostMicros30Days: number
  unattributedCampaignCostMicros30Days: number
  returnedSearchTermCount: number
  visibleSearchTermCount: number
}

export interface MetaAdResultDiagnostic {
  adId: string
  adName: string | null
  adSetId: string | null
  adSetName: string | null
  status: string | null
  effectiveStatus: string | null
  impressions: number
  clicks: number
  costMicros: number
  conversions: number
}

export interface CampaignProviderDiagnostics {
  googleSearch?: {
    searchTerms: GoogleSearchTermDiagnostic[]
    spendSummary?: GoogleSearchSpendSummary
    campaignNegatives?: GoogleSearchNegativeDiagnostic[]
  }
  metaAds?: {
    adResults: MetaAdResultDiagnostic[]
  }
}

export interface CampaignGenerationState {
  segments: Array<{
    segment: SiteSegmentRecord
    readiness: CampaignReadiness
    channels: CampaignChannelRecord[]
  }>
  channels: CampaignChannelRecord[]
  latestJob: JobRecord | null
}

export interface CampaignChannelDetailState {
  channel: CampaignChannelRecord
  entities: CampaignEntityRecord[]
  assets: SiteAssetRecord[]
  latestSnapshot: CampaignChannelSnapshotRecord | null
  providerDiagnostics: CampaignProviderDiagnostics | null
  latestOptimizationRun: OptimizationRunRecord | null
  latestOptimizationActions: AgentActionRecord[]
}

export interface QueuedCampaignLaunchResult {
  channels: CampaignChannelRecord[]
  job: JobRecord | null
  blockedByActiveSync?: ActiveCampaignSyncBlocker | null
}

export interface ActiveCampaignSyncBlocker {
  reason: "another_sync_running"
  job: JobRecord
  channels: CampaignChannelRecord[]
  message: string
}

export interface CampaignDetailProviderAccount {
  providerAccountId: string
  provider: string
  accountType: string
  externalAccountId: string
  label: string | null
  metadata: Record<string, unknown> | null
}

export interface CampaignConversionDefinitionRecord {
  conversionDefinitionId: string
  name: string
  status: string
  isPrimary: boolean
}

export type GenerationContextScope =
  | "segments"
  | "all_assets"
  | "landing"
  | "targeting"
  | "copy"
  | "media"

export interface GenerationContextReason {
  key: string
  points: number
}

export interface ScoredGenerationContextPage {
  crawlPageId: string
  siteId: string
  url: string
  status: string
  depth: number
  source: string
  title: string | null
  description: string | null
  excerpt: string | null
  canonicalUrl: string | null
  ogImageUrl: string | null
  contentR2Key: string | null
  indexedAt: string
  imageCount: number
  score: number
  reasons: GenerationContextReason[]
  selectedReason: string | null
}

export interface ScoredGenerationContextImage {
  crawlImageId: string
  siteId: string
  pageUrl: string
  imageUrl: string
  source: string
  altText: string | null
  width: number | null
  height: number | null
  mimeType: string | null
  sizeBytes: number | null
  contentR2Key: string | null
  score: number
  reasons: GenerationContextReason[]
  selectedReason: string | null
}

export interface GenerationContextSelection {
  site: Pick<SiteRecord, "siteId" | "name" | "primaryDomain" | "primaryGoal">
  segment: Pick<SiteSegmentRecord, "segmentId" | "name" | "segmentType"> | null
  scope: GenerationContextScope
  terms: string[]
  selectedPages: ScoredGenerationContextPage[]
  rankedPages: ScoredGenerationContextPage[]
  selectedImages: ScoredGenerationContextImage[]
  rankedImages: ScoredGenerationContextImage[]
}

export interface SiteCrawlPageRecord {
  crawlPageId: string
  siteId: string
  url: string
  status: string
  depth: number
  source: string
  title: string | null
  description: string | null
  excerpt: string | null
  canonicalUrl: string | null
  ogImageUrl: string | null
  etag: string | null
  lastModified: string | null
  httpStatus: number | null
  contentR2Key: string | null
  contentHash: string | null
  indexedAt: string
  errorMessage: string | null
}

export interface SiteIntelligenceRecord {
  siteId: string
  status: string
  summary: Record<string, unknown>
  generatedAt: string
  updatedAt: string
}

export interface JobRecord {
  jobId: string
  jobType: string
  status: string
  orgId: string | null
  siteId: string | null
  parentJobId: string | null
  payload: Record<string, unknown> | null
  result: Record<string, unknown> | null
  lockKey: string | null
  concurrencyKey: string | null
  concurrencyLimit: number | null
  enqueuePolicy: string
  attemptCount: number
  maxAttempts: number
  availableAt: string | null
  startedAt: string | null
  completedAt: string | null
  lastError: string | null
  createdAt: string
  updatedAt: string
}

export interface JobChildrenSummary {
  parentJobId: string
  total: number
  queued: number
  running: number
  stopping: number
  succeeded: number
  failed: number
  stopped: number
  active: number
  terminal: number
  allTerminal: boolean
}

export interface JobEntityState {
  jobId: string
  jobType: string
  status: string
  entityKey: string
  entityType: string | null
  entityId: string | null
  label: string | null
  lastError: string | null
  result: Record<string, unknown> | null
  depth: number
}

export interface JobActivityEvent {
  opsEventId: string
  jobId: string | null
  level: "info" | "warn" | "error"
  eventType: string
  message: string
  metadata: Record<string, unknown> | null
  createdAt: string
}

export interface JobTreeRecord {
  root: JobRecord
  children: Array<JobRecord & { depth: number }>
  summary: JobChildrenSummary
  rollupStatus: "queued" | "running" | "succeeded" | "failed" | "stopped"
  rowStates: JobEntityState[]
  activityEvents: JobActivityEvent[]
  latestError: string | null
}

export interface SiteGrowthAutomationState {
  latestJob: JobRecord | null
}

export interface SiteCrawlState {
  pages: SiteCrawlPageRecord[]
  intelligence: SiteIntelligenceRecord | null
  latestJob: JobRecord | null
}

export interface SearchConsoleMetricRow {
  key: string
  clicks: number
  impressions: number
  ctr: number
  position: number
}

export interface SiteSeoState {
  status: "not_linked" | "reconnect_required" | "available" | "provider_error"
  searchConsoleSiteUrl: string | null
  window: {
    startDate: string
    endDate: string
    days: number
  }
  totals: {
    clicks: number
    impressions: number
    ctr: number
    position: number
  } | null
  queries: SearchConsoleMetricRow[]
  pages: SearchConsoleMetricRow[]
  warning: string | null
  reconnectUrl: string | null
}

export interface SiteTrackingConfig {
  siteId: string
  publicKey: string
  status: string
  ga4MeasurementId: string | null
  metaPixelId: string | null
  piiMode: "hash"
  createdAt: string
  updatedAt: string
}

export interface ObservedEventPattern {
  patternId: string
  siteId: string
  eventName: string
  pageUrl: string | null
  elementTag: string | null
  elementText: string | null
  elementRole: string | null
  selectorHint: string | null
  accountLifecycle: string | null
  identityConfidence: string | null
  eventIntent: string | null
  classificationSource: string | null
  count: number
  sample: Record<string, unknown> | null
  firstSeenAt: string
  lastSeenAt: string
}

export interface CapturedEventSummary {
  summaryId: string
  label: string
  intent: "lead" | "purchase" | "signup" | "contact" | "account" | "navigation" | "engagement"
  status: "mapped" | "unmapped" | "disabled"
  optimizationCandidate: boolean
  totalCount: number
  pageUrl: string | null
  accountLifecycle: "new" | "existing" | "unknown" | null
  identityConfidence: "strong" | "anonymous" | "none" | null
  eventNames: string[]
  patternIds: string[]
  primaryPatternId: string
  suggestedConversionEventName: string | null
  suggestedGa4EventName: string | null
  suggestedMetaEventName: string | null
  rationale: string
  lastSeenAt: string
}

export interface JourneyStep {
  eventName: string
  pageUrl: string | null
  elementText: string | null
  occurredAt: string | null
}

export interface CapturedJourneySummary {
  journeySummaryId: string
  siteId: string
  journeyKey: string
  label: string
  intent: "lead" | "signup" | "account" | "engagement"
  accountLifecycle: "new" | "existing" | "unknown" | null
  confidence: number
  optimizationCandidate: boolean
  totalCount: number
  sessionCount: number
  sessionHashes: string[]
  sampleSequence: JourneyStep[]
  evidence: Record<string, unknown>
  suggestedConversionEventName: string | null
  suggestedGa4EventName: string | null
  suggestedMetaEventName: string | null
  rationale: string
  firstSeenAt: string
  lastSeenAt: string
  createdAt: string
  updatedAt: string
}

export interface SiteEventMapping {
  mappingId: string
  siteId: string
  patternId: string | null
  configHash: string | null
  name: string
  status: "active" | "disabled" | "deleted"
  source: "ai" | "manual" | "system"
  owner: "ai" | "user"
  conversionEventName: string
  ga4EventName: string
  metaEventName: string
  confidence: number | null
  rationale: string | null
  valueJson: Record<string, unknown> | null
  createdByJobId: string | null
  createdByUserId: string | null
  createdAt: string
  updatedAt: string
}

export type ConversionValidationRunStatus = "queued" | "running" | "succeeded" | "failed"
export type ConversionValidationResultStatus =
  | "not_configured"
  | "waiting"
  | "passed"
  | "warning"
  | "failed"
  | "pending_attribution"
  | "stale"

export interface ConversionValidationRun {
  validationRunId: string
  siteId: string
  mappingId: string
  jobId: string | null
  status: ConversionValidationRunStatus
  mode: "live_user_event" | "provider_preflight"
  windowMinutes: number
  requestedByUserId: string | null
  startedAt: string | null
  completedAt: string | null
  lastError: string | null
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface ConversionValidationResult {
  validationResultId: string
  validationRunId: string
  siteId: string
  mappingId: string
  provider: string
  providerAccountId: string | null
  externalAccountId: string | null
  status: ConversionValidationResultStatus
  checkedAt: string | null
  lastPassedAt: string | null
  message: string
  metadata: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export interface ConversionValidationSummary {
  run: ConversionValidationRun
  results: ConversionValidationResult[]
}

export interface SiteTrackingPayload {
  config: SiteTrackingConfig
  installSnippet: string
  observedPatterns: ObservedEventPattern[]
  eventSummaries: CapturedEventSummary[]
  journeySummaries: CapturedJourneySummary[]
  mappings: SiteEventMapping[]
  conversionValidation: ConversionValidationSummary | null
}

export interface RealtimeTrackingEventCount {
  eventName: string
  count: number
}

export interface RealtimeTrackingEventsPayload {
  propertyId: string
  minutes: number
  totalCount: number
  generatedAt: string
  events: RealtimeTrackingEventCount[]
}

export interface CreateAdgiroMeasurementPayload {
  providerLink: SiteProviderLinkRecord
  config: SiteTrackingConfig
}

export async function apiGet<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  })

  if (!response.ok) {
    redirectToLoginOnAuthFailure(response)
    throw new Error(await responseErrorMessage(response))
  }

  const payload = (await response.json()) as ApiEnvelope<T>
  return payload.data
}

export async function apiPost<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    redirectToLoginOnAuthFailure(response)
    throw new Error(await responseErrorMessage(response))
  }

  const payload = (await response.json()) as ApiEnvelope<T>
  return payload.data
}

export async function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: "PATCH",
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(body === undefined ? {} : { "content-type": "application/json" }),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (!response.ok) {
    redirectToLoginOnAuthFailure(response)
    throw new Error(await responseErrorMessage(response))
  }

  const payload = (await response.json()) as ApiEnvelope<T>
  return payload.data
}

export async function apiDelete<T>(path: string): Promise<T> {
  const response = await fetch(path, {
    method: "DELETE",
    credentials: "same-origin",
    headers: { accept: "application/json" },
  })

  if (!response.ok) {
    redirectToLoginOnAuthFailure(response)
    throw new Error(await responseErrorMessage(response))
  }

  const payload = (await response.json()) as ApiEnvelope<T>
  return payload.data
}

export async function apiUpload<T>(path: string, body: FormData): Promise<T> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { accept: "application/json" },
    body,
  })

  if (!response.ok) {
    redirectToLoginOnAuthFailure(response)
    throw new Error(await responseErrorMessage(response))
  }

  const payload = (await response.json()) as ApiEnvelope<T>
  return payload.data
}

async function responseErrorMessage(response: Response): Promise<string> {
  let text = ""
  try {
    text = await response.text()
  } catch {}
  if (text.trim()) {
    try {
      const payload = JSON.parse(text) as unknown
      const message = errorMessageFromPayload(payload)
      if (message) return message
    } catch {}
    if (!response.headers.get("content-type")?.includes("text/html")) return text.trim()
  }
  return `Request failed: ${response.status}`
}

export function errorMessageFromPayload(payload: unknown): string | null {
  if (!payload || typeof payload !== "object") return null
  const record = payload as Record<string, unknown>
  const message = stringValue(record.message)
  if (message) return message

  const error = record.error
  if (error && typeof error === "object") {
    const errorRecord = error as Record<string, unknown>
    const errorMessage = stringValue(errorRecord.message)
    if (errorMessage) return errorMessage
    const issueMessage = messageFromIssues(errorRecord.issues)
    if (issueMessage) return issueMessage
  }

  return messageFromIssues(record.issues)
}

function messageFromIssues(value: unknown): string | null {
  if (!Array.isArray(value) || value.length === 0) return null
  const firstIssue = value[0]
  if (!firstIssue || typeof firstIssue !== "object") return "Invalid request."
  const issue = firstIssue as Record<string, unknown>
  const message = stringValue(issue.message) ?? "Invalid value"
  const path = Array.isArray(issue.path)
    ? issue.path
        .map((part) => String(part))
        .filter(Boolean)
        .join(".")
    : ""
  return path ? `Invalid request: ${path} ${message}.` : `Invalid request: ${message}.`
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null
}

function redirectToLoginOnAuthFailure(response: Response) {
  if (response.status !== 401 || typeof window === "undefined") return
  const next = `${window.location.pathname}${window.location.search}`
  const loginUrl = new URL("/login", window.location.origin)
  if (next.startsWith("/app") && !next.startsWith("//")) {
    loginUrl.searchParams.set("next", next)
  }
  window.location.assign(loginUrl.toString())
}
